// Step 2: put the site on the map. The site's coordinates always come first, so the
// map sits in exactly the right place. The picture is the free satellite picture as
// standard, or the client's own map if they ask (agreed 29 Sep).

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { SiteDraft } from '../../../../src/draft.ts';
import { api, ApiError, message } from '../../api.ts';
import { toB64u } from '../../bytes.ts';
import { day } from '../../format.ts';
import { mapSize, project, type BBox } from '../../map.tsx';
import type { SiteView } from '../../types.ts';

type Scene = { id: string; date: string; cloudCover: number };
type Corner = 'nw' | 'ne' | 'sw' | 'se' | 'move';

const km = (b: BBox) => {
  const mid = (((b[1] + b[3]) / 2) * Math.PI) / 180;
  return { w: (b[2] - b[0]) * 111.32 * Math.cos(mid), h: (b[3] - b[1]) * 110.574 };
};
const grow = (b: BBox, f: number): BBox => {
  const cx = (b[0] + b[2]) / 2;
  const cy = (b[1] + b[3]) / 2;
  return [cx - ((b[2] - b[0]) * f) / 2, cy - ((b[3] - b[1]) * f) / 2, cx + ((b[2] - b[0]) * f) / 2, cy + ((b[3] - b[1]) * f) / 2];
};
const fmt = (n: number) => n.toFixed(4);
const parsePair = (t: string): { lat: number; lon: number } | null => {
  const m = t.trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*[,;\s]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  return m ? { lat: Number(m[1]), lon: Number(m[2]) } : null;
};

async function jpegFrom(res: Response): Promise<string> {
  return URL.createObjectURL(await res.blob());
}

export function MapStep({ site, reload }: { site: SiteView; reload: () => void }) {
  const draft = site.draft as SiteDraft;
  const saved = draft.picture ?? null;
  const [text, setText] = useState(draft.map ? `${fmt((draft.map.bbox[1] + draft.map.bbox[3]) / 2)}, ${fmt((draft.map.bbox[0] + draft.map.bbox[2]) / 2)}` : '');
  const [mode, setMode] = useState<'satellite' | 'client'>(saved?.source === 'client' ? 'client' : 'satellite');
  const [area, setArea] = useState<BBox | null>((draft.map?.bbox as BBox | undefined) ?? null);
  const [context, setContext] = useState<BBox | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [sceneId, setSceneId] = useState<string | null>(null);
  const [datesOpen, setDatesOpen] = useState(false);
  const [night, setNight] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // The client's own map
  const [file, setFile] = useState<{ url: string; bytes: Uint8Array; width: number; height: number; name: string } | null>(null);
  const [topLeft, setTopLeft] = useState('');
  const [bottomRight, setBottomRight] = useState('');
  const [blend, setBlend] = useState(0.6);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<{ kind: Corner; start: [number, number]; startArea: BBox } | null>(null);

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : message(e));
    } finally {
      setBusy(null);
    }
  };

  const loadPreview = async (ctx: BBox, scene: string | null) => {
    const res = await fetch('/console-api/map/preview', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ bbox: ctx, sceneId: scene }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new ApiError(res.status, data.error ?? 'error', data.message ?? "The satellite picture couldn't be downloaded.");
    }
    setPreview(await jpegFrom(res));
    setContext(ctx);
  };

  const find = () =>
    run('Finding the site and the newest clear satellite picture… about 10 seconds', async () => {
      const at = await api<{ lat: number; lon: number; bbox: BBox }>('POST', '/map/locate', { text });
      const first = await api<{ scenes: Scene[] }>('POST', '/map/choices', { bbox: at.bbox });
      setScenes(first.scenes);
      setSceneId(first.scenes[0].id);
      setArea(at.bbox);
      try {
        await loadPreview(grow(at.bbox, 1.6), first.scenes[0].id);
      } catch {
        await loadPreview(at.bbox, first.scenes[0].id); // near a picture's edge: show just the area
      }
    });

  const widen = () =>
    run('Fetching a wider view… about 10 seconds', async () => {
      if (area) await loadPreview(grow(area, 1.8), sceneId);
    });

  const pickDate = (id: string) =>
    run('Fetching that picture… about 10 seconds', async () => {
      setSceneId(id);
      setDatesOpen(false);
      if (context) await loadPreview(context, id);
    });

  const openDates = () =>
    run('Checking for other clear pictures…', async () => {
      if (!area) return;
      const r = await api<{ scenes: Scene[] }>('POST', '/map/choices', { bbox: area });
      setScenes(r.scenes);
      setDatesOpen(true);
    });

  const useSatellite = () =>
    run('Making the map picture… about 10 seconds', async () => {
      const r = await api<{ picture: { capturedOn: string } }>('POST', `/sites/${site.siteId}/map/satellite`, { bbox: area, sceneId });
      setDone(`Map picture saved: satellite picture from ${day(r.picture.capturedOn)}.`);
      reload();
    });

  const chooseFile = async (f: File | undefined) => {
    setError(null);
    if (!f) return;
    if (!/^image\/(png|jpeg)$/.test(f.type)) {
      setError('For now the map has to be a PNG or JPG picture.');
      return;
    }
    const bytes = new Uint8Array(await f.arrayBuffer());
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => setFile({ url, bytes, width: img.naturalWidth, height: img.naturalHeight, name: f.name });
    img.src = url;
    if (area && !topLeft) {
      setTopLeft(`${fmt(area[3])}, ${fmt(area[0])}`);
      setBottomRight(`${fmt(area[1])}, ${fmt(area[2])}`);
    }
  };

  const tl = parsePair(topLeft);
  const br = parsePair(bottomRight);
  const clientArea: BBox | null = tl && br && tl.lon < br.lon && br.lat < tl.lat ? [tl.lon, br.lat, br.lon, tl.lat] : null;
  const stretch = file && clientArea ? file.width / file.height / (km(clientArea).w / km(clientArea).h) : 1;
  const stretched = stretch < 0.85 || stretch > 1.15;

  const useClient = () =>
    run('Saving the client’s map…', async () => {
      if (!file || !tl || !br) return;
      await api('POST', `/sites/${site.siteId}/map/client`, { fileBase64url: toB64u(file.bytes), topLeft: tl, bottomRight: br });
      setDone('Map picture saved: the client’s own map.');
      reload();
    });

  // ---- Dragging the map area's corners ----
  const toLonLat = (clientX: number, clientY: number): [number, number] | null => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm || !context) return null;
    const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    const { w, h } = mapSize(context);
    return [context[0] + (pt.x / w) * (context[2] - context[0]), context[3] - (pt.y / h) * (context[3] - context[1])];
  };
  const startDrag = (kind: Corner) => (e: ReactPointerEvent) => {
    const at = toLonLat(e.clientX, e.clientY);
    if (!at || !area) return;
    svgRef.current?.setPointerCapture(e.pointerId);
    drag.current = { kind, start: at, startArea: area };
    e.preventDefault();
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    const at = toLonLat(e.clientX, e.clientY);
    if (!d || !at || !context) return;
    let [w, s, ee, n] = d.startArea;
    if (d.kind === 'move') {
      const dx = at[0] - d.start[0];
      const dy = at[1] - d.start[1];
      [w, s, ee, n] = [w + dx, s + dy, ee + dx, n + dy];
    } else {
      if (d.kind === 'nw' || d.kind === 'sw') w = at[0];
      if (d.kind === 'ne' || d.kind === 'se') ee = at[0];
      if (d.kind === 'nw' || d.kind === 'ne') n = at[1];
      if (d.kind === 'sw' || d.kind === 'se') s = at[1];
    }
    const minLon = 0.004;
    const minLat = 0.004;
    if (ee - w < minLon || n - s < minLat) return;
    // Stay inside what's shown.
    w = Math.max(w, context[0]);
    ee = Math.min(ee, context[2]);
    s = Math.max(s, context[1]);
    n = Math.min(n, context[3]);
    setArea([w, s, ee, n]);
  };
  const endDrag = () => {
    drag.current = null;
  };

  const shown = context ?? area;
  const size = shown ? mapSize(shown) : null;
  const rect = area && shown ? [project(shown, area[0], area[3]), project(shown, area[2], area[1])] : null;
  const clientRect = clientArea && shown ? [project(shown, clientArea[0], clientArea[3]), project(shown, clientArea[2], clientArea[1])] : null;
  const savedMatchesArea = saved && area && draft.map && draft.map.bbox.every((v, i) => Math.abs(v - (area as BBox)[i]) < 1e-9);
  const background = preview ?? (saved && savedMatchesArea ? `/console-api/pictures/${saved.sha256}` : null);
  const imgFilter = night ? 'saturate(0.55) brightness(0.42) contrast(1.3)' : 'brightness(0.85)';

  return (
    <>
      <div className="stack" style={{ gap: 6 }}>
        <label className="muted small" htmlFor="where">
          Where the site is: always needed, so the map sits in exactly the right place
        </label>
        <div className="row">
          <input
            id="where"
            className="input mono"
            style={{ flex: 1 }}
            placeholder="30.4158, 47.2858, or a Google or Apple Maps link"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !busy && text.trim() && find()}
          />
          <button className="btn primary" onClick={find} disabled={!!busy || !text.trim()}>
            Find
          </button>
        </div>
      </div>

      <div className="seg" role="group" aria-label="Map picture">
        <button className={mode === 'satellite' ? 'on' : ''} aria-pressed={mode === 'satellite'} onClick={() => setMode('satellite')}>
          Satellite picture
        </button>
        <button className={mode === 'client' ? 'on' : ''} aria-pressed={mode === 'client'} onClick={() => setMode('client')}>
          Client's own map
        </button>
      </div>

      {busy && (
        <div className="spinner-note" role="status">
          <span className="dot" style={{ background: 'var(--amber)' }} /> {busy}
        </div>
      )}
      {error && (
        <div className="note red" role="alert">
          {error}
        </div>
      )}
      {done && <div className="note green">{done}</div>}

      <div className="split wide-left">
        <div className="stack">
          {shown && size && background ? (
            <div className="editor">
              <svg ref={svgRef} viewBox={`0 0 ${size.w} ${size.h}`} onPointerMove={onMove} onPointerUp={endDrag} onPointerCancel={endDrag} role="img" aria-label="The map area on the satellite picture">
                <image href={background} x="0" y="0" width={size.w} height={size.h} preserveAspectRatio="none" style={{ filter: imgFilter }} />
                {mode === 'client' && file && clientRect && (
                  <image
                    href={file.url}
                    x={clientRect[0][0]}
                    y={clientRect[0][1]}
                    width={clientRect[1][0] - clientRect[0][0]}
                    height={clientRect[1][1] - clientRect[0][1]}
                    preserveAspectRatio="none"
                    opacity={blend}
                  />
                )}
                {rect && (
                  <>
                    <rect
                      x={rect[0][0]}
                      y={rect[0][1]}
                      width={rect[1][0] - rect[0][0]}
                      height={rect[1][1] - rect[0][1]}
                      fill="rgba(255,255,255,0.04)"
                      stroke="#FFFFFF"
                      strokeWidth="3"
                      strokeDasharray="14 8"
                      style={{ cursor: preview ? 'move' : 'default' }}
                      onPointerDown={preview ? startDrag('move') : undefined}
                    />
                    {preview &&
                      (
                        [
                          ['nw', rect[0][0], rect[0][1]],
                          ['ne', rect[1][0], rect[0][1]],
                          ['sw', rect[0][0], rect[1][1]],
                          ['se', rect[1][0], rect[1][1]],
                        ] as [Corner, number, number][]
                      ).map(([k, x, y]) => <rect key={k} className={`handle ${k}`} x={x - 11} y={y - 11} width="22" height="22" rx="3" onPointerDown={startDrag(k)} />)}
                  </>
                )}
              </svg>
              {area && rect && size && (
                <span className="chip-on-map" style={{ left: `calc(${(rect[0][0] / size.w) * 100}% + 12px)`, top: `calc(${(rect[0][1] / size.h) * 100}% + 12px)` }}>
                  Map area · {km(area).w.toFixed(1)} × {km(area).h.toFixed(1)} km
                </span>
              )}
            </div>
          ) : (
            <div className="card empty" style={{ minHeight: 320, justifyContent: 'center' }}>
              Type the site's coordinates and press Find. The client usually sends them before set-up.
            </div>
          )}
          {shown && <p className="faint small">{mode === 'client' && blend === 1 ? 'The client’s map' : 'Contains modified Copernicus Sentinel data 2026'}</p>}
        </div>

        <div className="stack">
          {mode === 'satellite' ? (
            <>
              <div className="card">
                <span className="card-label">Satellite picture · the standard</span>
                <span className="card-value">
                  {sceneId ? `${day(scenes.find((s) => s.id === sceneId)?.date)} · clear` : saved?.source === 'satellite' ? `${day(saved.capturedOn)} · clear` : '—'}
                </span>
                <span className="muted small">Sentinel-2, European Space Agency · 10 m per pixel. The newest clear picture is picked for you.</span>
                {area && (
                  <button className="btn small" style={{ alignSelf: 'flex-start' }} onClick={openDates} disabled={!!busy}>
                    Pick another date
                  </button>
                )}
                {datesOpen && (
                  <div className="stack" style={{ gap: 4 }}>
                    {scenes.map((s) => (
                      <button key={s.id} className={s.id === sceneId ? 'pill on' : 'pill'} style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center' }} onClick={() => pickDate(s.id)}>
                        <span>{day(s.date)}</span>
                        <span className="small">{s.cloudCover}% cloud</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="card">
                <span className="card-label">Map area</span>
                <span className="card-value">{area ? `${km(area).w.toFixed(1)} × ${km(area).h.toFixed(1)} km` : '—'}</span>
                <span className="muted small">Everything the tablet and phones show. Drag a corner or the whole box to change it; every zone has to fit inside.</span>
                {preview && (
                  <button className="btn small" style={{ alignSelf: 'flex-start' }} onClick={widen} disabled={!!busy}>
                    Show a wider view
                  </button>
                )}
                <div className="row" style={{ justifyContent: 'space-between', paddingTop: 6, borderTop: '1px solid var(--line)' }}>
                  <span className="small">Night view, as staff see it</span>
                  <button className="switch" aria-label="Night view" aria-pressed={night} onClick={() => setNight(!night)}>
                    <span />
                  </button>
                </div>
              </div>
              <button className="btn primary" disabled={!!busy || !preview || !area} onClick={useSatellite}>
                Use this map area
              </button>
              {saved && !preview && <p className="faint small">Saved: {saved.source === 'satellite' ? `satellite picture from ${day(saved.capturedOn)}` : 'the client’s own map'}.</p>}
            </>
          ) : (
            <>
              <div className="card" style={{ borderStyle: file ? 'solid' : 'dashed' }}>
                <span className="card-value" style={{ fontSize: 16 }}>
                  Only if the client asks to use their own map
                </span>
                <label className="btn small" style={{ alignSelf: 'flex-start' }}>
                  {file ? 'Choose another file' : 'Upload the client’s map'}
                  <input type="file" accept="image/png,image/jpeg" className="visually-hidden" onChange={(e) => chooseFile(e.target.files?.[0])} />
                </label>
                <span className="faint small">PNG or JPG for now, up to 12 MB. GeoTIFF and PDF plans are next.</span>
                {file && (
                  <span className="mono small">
                    {file.name} · {file.width} × {file.height}
                  </span>
                )}
              </div>
              {file && (
                <div className="card">
                  <span style={{ fontWeight: 500 }}>Where it sits: its corners' coordinates</span>
                  <label className="field">
                    Top-left corner (latitude, longitude)
                    <input className="input mono" value={topLeft} onChange={(e) => setTopLeft(e.target.value)} />
                  </label>
                  <label className="field">
                    Bottom-right corner (latitude, longitude)
                    <input className="input mono" value={bottomRight} onChange={(e) => setBottomRight(e.target.value)} />
                  </label>
                  {clientArea && stretched && <p className="error">Those corners don't match the map's shape, so it would come out stretched. Check the two corners.</p>}
                  <span className="small">Check it lines up with the satellite picture</span>
                  <div className="seg" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                    {(
                      [
                        [0, 'Satellite'],
                        [0.6, 'Both'],
                        [1, 'Client’s map'],
                      ] as [number, string][]
                    ).map(([v, label]) => (
                      <button key={label} className={blend === v ? 'on' : ''} aria-pressed={blend === v} onClick={() => setBlend(v)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="note amber">Anyone who scans the site's QR code gets this map, the same as staff. Check the client is happy with that.</div>
              <button className="btn primary" disabled={!!busy || !file || !clientArea || stretched} onClick={useClient}>
                Use the client's map
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
